import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Modal,
  Form,
  Spinner,
  Badge,
  InputGroup,
  Card,
  Row,
  Col,
  OverlayTrigger,
  Tooltip,
  Alert,
} from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faMinus,
  faTrashAlt,
  faLayerGroup,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import { useCookies } from "react-cookie";
import * as classicReturnApi from "../../../../controller/api/classicReturn";

const RETURN_REASONS = [
  { key: "BAN_E", label: "Bán ế" },
  { key: "HET_VE", label: "Hết vé" },
  { key: "VE_HU", label: "Vé hư" },
  { key: "LY_DO_KHAC", label: "Lý do khác" },
];

export default function BulkReturnPopup({
  show,
  onHide,
  selectedTickets,
  onSuccess,
}) {
  const [cookies] = useCookies(["lottmekong"]);
  const [reason, setReason] = useState("BAN_E");
  const [reasonDetail, setReasonDetail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState(1); // Step 1: confirm & edit quantities, Step 2: select reason

  // State for editable return amounts - Map<ticketId, returnAmount>
  const [returnAmounts, setReturnAmounts] = useState({});

  // Initialize return amounts when selectedTickets changes
  useEffect(() => {
    if (selectedTickets && selectedTickets.length > 0) {
      const initialAmounts = {};
      selectedTickets.forEach((ticket) => {
        initialAmounts[ticket.id] = ticket.amount; // Default to full amount
      });
      setReturnAmounts(initialAmounts);
    }
  }, [selectedTickets]);

  const totalTickets = selectedTickets?.length || 0;

  // Calculate total based on editable return amounts
  const totalReturnAmount = useMemo(() => {
    return Object.values(returnAmounts).reduce(
      (sum, amount) => sum + (amount || 0),
      0,
    );
  }, [returnAmounts]);

  // Get list of tickets with their return amounts for submission
  const ticketsToReturn = useMemo(() => {
    if (!selectedTickets) return [];
    return selectedTickets
      .filter((ticket) => returnAmounts[ticket.id] > 0)
      .map((ticket) => ({
        ...ticket,
        returnAmount: returnAmounts[ticket.id],
      }));
  }, [selectedTickets, returnAmounts]);

  const handleAmountChange = (ticketId, newAmount, maxAmount) => {
    const value = Math.max(0, Math.min(parseInt(newAmount) || 0, maxAmount));
    setReturnAmounts((prev) => ({
      ...prev,
      [ticketId]: value,
    }));
  };

  const handleIncrement = (ticketId, maxAmount) => {
    setReturnAmounts((prev) => ({
      ...prev,
      [ticketId]: Math.min((prev[ticketId] || 0) + 1, maxAmount),
    }));
  };

  const handleDecrement = (ticketId) => {
    setReturnAmounts((prev) => ({
      ...prev,
      [ticketId]: Math.max((prev[ticketId] || 0) - 1, 0),
    }));
  };

  const handleSetAll = () => {
    const newAmounts = {};
    selectedTickets?.forEach((ticket) => {
      newAmounts[ticket.id] = ticket.amount;
    });
    setReturnAmounts(newAmounts);
  };

  const handleSetAllToValue = (value) => {
    const newAmounts = {};
    selectedTickets?.forEach((ticket) => {
      // Set to the value, but cap at ticket's max amount
      newAmounts[ticket.id] = Math.min(value, ticket.amount);
    });
    setReturnAmounts(newAmounts);
  };

  const handleClearAll = () => {
    const newAmounts = {};
    selectedTickets?.forEach((ticket) => {
      newAmounts[ticket.id] = 0;
    });
    setReturnAmounts(newAmounts);
  };

  const handleSubmit = async () => {
    if (ticketsToReturn.length === 0) {
      setError("Vui lòng chọn ít nhất 1 vé để hoàn");
      return;
    }

    if (reason === "LY_DO_KHAC" && !reasonDetail.trim()) {
      setError("Vui lòng nhập chi tiết lý do");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const returnData = ticketsToReturn.map((ticket) => ({
        storageId: ticket.id,
        returnAmount: ticket.returnAmount,
        reason: reason,
        reasonDetail: reason === "LY_DO_KHAC" ? reasonDetail : null,
      }));

      const result = await classicReturnApi.addBulkReturn(cookies.lottmekong, {
        returns: returnData,
      });

      if (result && result.success) {
        const successCount = result.successCount || ticketsToReturn.length;
        const failCount = result.failCount || 0;

        if (failCount > 0) {
          alert(
            `Hoàn vé thành công: ${successCount}/${ticketsToReturn.length} vé.\nThất bại: ${failCount} vé.`,
          );
        } else {
          alert(
            `Hoàn vé nhanh thành công!\n${successCount} vé đã được hoàn với tổng ${totalReturnAmount} tờ.`,
          );
        }
        onSuccess && onSuccess();
        handleClose();
      } else {
        setError(result?.message || "Có lỗi xảy ra khi hoàn vé");
      }
    } catch (err) {
      console.error("Bulk return error:", err);
      setError("Lỗi kết nối");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setReason("BAN_E");
    setReasonDetail("");
    setError("");
    setStep(1);
    setReturnAmounts({});
    onHide();
  };

  const handleNextStep = () => {
    if (ticketsToReturn.length === 0) {
      setError("Vui lòng nhập số lượng hoàn cho ít nhất 1 vé");
      return;
    }
    setError("");
    setStep(2);
  };

  const handlePrevStep = () => {
    setStep(1);
    setError("");
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      backdrop="static"
      className="modal-modern"
      dialogClassName="modal-90w"
    >
      <Modal.Header className="border-0 pb-0 bg-white">
        <div className="w-100 d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-1 fw-bold text-primary">
              <FontAwesomeIcon icon={faLayerGroup} className="me-2" />
              Hoàn trả vé hàng loạt
            </h5>
            <p className="text-muted small mb-0">
              {step === 1
                ? "Bước 1: Chọn số lượng vé cần hoàn"
                : "Bước 2: Chọn lý do và xác nhận"}
            </p>
          </div>
          <Button variant="close" onClick={handleClose} aria-label="Close" />
        </div>
      </Modal.Header>

      <Modal.Body className="pt-2 bg-light">
        {/* Progress Indicator */}
        <div className="d-flex align-items-center mb-4 px-2">
          <div
            className={`rounded-circle d-flex align-items-center justify-content-center fw-bold ${step >= 1 ? "bg-primary text-white" : "bg-secondary text-white"}`}
            style={{ width: 32, height: 32, fontSize: 14 }}
          >
            1
          </div>
          <div className="flex-grow-1 mx-2 bg-gray-300" style={{ height: 3 }}>
            <div
              className="bg-primary h-100 transition-all"
              style={{
                width: step === 2 ? "100%" : "0%",
                transition: "width 0.3s ease",
              }}
            ></div>
          </div>
          <div
            className={`rounded-circle d-flex align-items-center justify-content-center fw-bold ${step >= 2 ? "bg-primary text-white" : "bg-gray-400 text-white"}`}
            style={{ width: 32, height: 32, fontSize: 14 }}
          >
            2
          </div>
        </div>

        {step === 1 ? (
          <>
            {/* Summary & Quick Actions Card */}
            <Card className="border-0 shadow-sm mb-3">
              <Card.Body className="p-3">
                <Row className="align-items-center g-3">
                  <Col md={12} lg={6}>
                    <div className="d-flex align-items-center">
                      <div className="icon-shape icon-sm bg-light-primary text-primary rounded-circle me-3">
                        <span className="fw-bold">{totalTickets}</span>
                      </div>
                      <div>
                        <div className="small text-muted fw-bold">
                          TỔNG VÉ ĐÃ CHỌN
                        </div>
                        <div className="small text-muted">
                          Đang chọn hoàn:{" "}
                          <b className="text-danger">
                            {ticketsToReturn.length}
                          </b>{" "}
                          vé ({totalReturnAmount} tờ)
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col md={12} lg={6}>
                    <div className="bg-light p-2 rounded border border-light d-flex align-items-center">
                      <div className="me-2 text-nowrap small fw-bold text-muted">
                        ⚡ Nhập nhanh:
                      </div>
                      <InputGroup size="sm">
                        <Form.Control
                          type="number"
                          placeholder="SL"
                          min={1}
                          className="border-end-0"
                          style={{ maxWidth: "70px" }}
                          id="bulkAmountInput"
                          onKeyPress={(e) => {
                            if (e.key === "Enter") {
                              const val = parseInt(e.target.value) || 0;
                              if (val > 0) {
                                handleSetAllToValue(val);
                                e.target.value = "";
                              }
                            }
                          }}
                        />
                        <Button
                          variant="outline-primary"
                          onClick={() => {
                            const input =
                              document.getElementById("bulkAmountInput");
                            const val = parseInt(input.value) || 0;
                            if (val > 0) {
                              handleSetAllToValue(val);
                              input.value = "";
                            }
                          }}
                        >
                          Áp dụng
                        </Button>
                      </InputGroup>
                      <OverlayTrigger
                        overlay={<Tooltip>Hoàn tất cả vé tối đa</Tooltip>}
                      >
                        <Button
                          variant="link"
                          className="text-primary ms-1 p-1"
                          onClick={handleSetAll}
                        >
                          <small className="fw-bold">Tất cả</small>
                        </Button>
                      </OverlayTrigger>
                      <OverlayTrigger overlay={<Tooltip>Xóa lựa chọn</Tooltip>}>
                        <Button
                          variant="link"
                          className="text-danger ms-1 p-1"
                          onClick={handleClearAll}
                        >
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </Button>
                      </OverlayTrigger>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Ticket List */}
            <div
              style={{ maxHeight: "400px", overflowY: "auto", paddingRight: 5 }}
              className="custom-scrollbar"
            >
              {selectedTickets?.map((ticket) => {
                const currentAmount = returnAmounts[ticket.id] || 0;
                const isZero = currentAmount === 0;
                const max = ticket.amount;

                return (
                  <Card
                    key={ticket.id}
                    className={`border-0 shadow-sm mb-2 ${isZero ? "opacity-75" : "border-start border-3 border-primary"}`}
                  >
                    <Card.Body className="p-3">
                      <Row className="align-items-center">
                        <Col xs={12} sm={5} className="mb-2 mb-sm-0">
                          <div className="d-flex align-items-center">
                            <Form.Check
                              type="checkbox"
                              checked={!isZero}
                              onChange={(e) => {
                                if (e.target.checked)
                                  handleAmountChange(ticket.id, max, max);
                                else handleAmountChange(ticket.id, 0, max);
                              }}
                              className="me-3 transform-scale-12"
                              style={{ transform: "scale(1.2)" }}
                            />
                            <div>
                              <div className="d-flex align-items-center gap-2 mb-1">
                                <Badge
                                  bg="light"
                                  text="dark"
                                  className="border"
                                >
                                  #{ticket.id.slice(0, 8)}
                                </Badge>
                                <Badge
                                  bg={
                                    ticket.channelName === "Miền Bắc"
                                      ? "danger"
                                      : "success"
                                  }
                                  className="fw-normal"
                                >
                                  {ticket.channelName}
                                </Badge>
                              </div>
                              <div className="small text-muted">
                                {ticket.resultDate}
                              </div>
                            </div>
                          </div>
                        </Col>

                        <Col
                          xs={12}
                          sm={4}
                          className="mb-2 mb-sm-0 text-center"
                        >
                          <div className="fw-bold fs-5 font-monospace text-dark letter-spacing-2">
                            {(ticket.numbers || "").split("").join(" ")}
                          </div>
                        </Col>

                        <Col xs={12} sm={3}>
                          <div className="d-flex justify-content-end align-items-center">
                            <div className="d-flex flex-column align-items-end me-2">
                              <small
                                className="text-muted"
                                style={{ fontSize: 10 }}
                              >
                                Tối đa: {max}
                              </small>
                            </div>
                            <div
                              className="input-group input-group-sm"
                              style={{ width: 110 }}
                            >
                              <Button
                                variant="outline-gray-400"
                                className="border-end-0 text-dark"
                                onClick={() => handleDecrement(ticket.id)}
                                disabled={currentAmount <= 0}
                              >
                                <FontAwesomeIcon icon={faMinus} size="xs" />
                              </Button>
                              <Form.Control
                                type="number"
                                className="text-center fw-bold border-gray-400"
                                value={currentAmount}
                                onChange={(e) =>
                                  handleAmountChange(
                                    ticket.id,
                                    e.target.value,
                                    max,
                                  )
                                }
                                style={{ zIndex: 0 }}
                              />
                              <Button
                                variant="outline-gray-400"
                                className="border-start-0 text-dark"
                                onClick={() =>
                                  handleIncrement(ticket.id, ticket.amount)
                                }
                                disabled={currentAmount >= max}
                              >
                                <FontAwesomeIcon icon={faPlus} size="xs" />
                              </Button>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                );
              })}
            </div>
          </>
        ) : (
          <>
            {/* Step 2: Confirm */}
            <Card className="border-0 shadow-sm mb-4 bg-white">
              <Card.Body className="p-4 text-center">
                <div className="display-4 fw-bold text-primary mb-2">
                  {totalReturnAmount}
                </div>
                <div className="text-muted fw-bold">TỔNG SỐ TỜ VÉ HOÀN</div>
                <div className="mt-2 badge bg-light text-dark border px-3 py-2">
                  Tổng: {ticketsToReturn.length} mã vé
                </div>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-4">
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">
                    Lý do hoàn vé <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    size="lg"
                    className="form-select-lg border-primary"
                  >
                    {RETURN_REASONS.map((r) => (
                      <option key={r.key} value={r.key}>
                        {r.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                {reason === "LY_DO_KHAC" && (
                  <Form.Group className="mb-3 animate-fade-in">
                    <Form.Label className="fw-bold">Chi tiết lý do</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={reasonDetail}
                      onChange={(e) => setReasonDetail(e.target.value)}
                      placeholder="Nhập chi tiết lý do hoàn vé..."
                      className="bg-light"
                    />
                  </Form.Group>
                )}
              </Card.Body>
            </Card>

            {error && (
              <Alert variant="danger" className="motion-safe-animate-pulse">
                {error}
              </Alert>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="bg-white border-top-0 py-3">
        {step === 1 ? (
          <div className="w-100 d-flex justify-content-between">
            <Button
              variant="white"
              className="text-muted"
              onClick={handleClose}
            >
              Hủy bỏ
            </Button>
            <Button
              variant="primary"
              onClick={handleNextStep}
              disabled={ticketsToReturn.length === 0}
              className="px-4 fw-bold shadow-sm"
            >
              Tiếp tục <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
            </Button>
          </div>
        ) : (
          <div className="w-100 d-flex justify-content-between">
            <Button variant="white" onClick={handlePrevStep} disabled={loading}>
              Quay lại
            </Button>
            <Button
              variant="danger"
              onClick={handleSubmit}
              disabled={loading}
              className="px-4 fw-bold shadow-sm"
            >
              {loading ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Đang xử lý...
                </>
              ) : (
                `Xác nhận hoàn (${totalReturnAmount} tờ)`
              )}
            </Button>
          </div>
        )}
      </Modal.Footer>
    </Modal>
  );
}
